import React, { useCallback, useState } from 'react';
import { Table, Button, Input, Space, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { FT162FormItem, FT163FormItem, T206FormData } from '../hooks/useT206Record';

const { TextArea } = Input;

interface TrackingModalProps {
  topicIdx: number;
  topicTitle: string;
  items: FT163FormItem[];
  isEditing: boolean;
  onClose: () => void;
  onChange: (topicIdx: number, items: FT163FormItem[]) => void;
}

const TrackingModal: React.FC<TrackingModalProps> = ({
  topicIdx,
  topicTitle,
  items,
  isEditing,
  onClose,
  onChange,
}) => {
  const handleChange = useCallback((idx: number, key: keyof FT163FormItem, value: string) => {
    const updated = items.map((row, i) =>
      i === idx ? { ...row, [key]: value } : row
    );
    onChange(topicIdx, updated);
  }, [items, onChange, topicIdx]);

  const handleAdd = useCallback(() => {
    onChange(topicIdx, [
      ...items,
      { ft163_11: '', ft163_12: '', ft163_13: '', ft163_15: '', ft163_15a: '', ft163_16: '', ft163_16a: '' },
    ]);
  }, [items, onChange, topicIdx]);

  const handleDelete = useCallback((idx: number) => {
    onChange(topicIdx, items.filter((_, i) => i !== idx));
  }, [items, onChange, topicIdx]);

  const columns = [
    { title: '序', width: 40, render: (_: unknown, __: unknown, idx: number) => idx + 1 },
    {
      title: '追蹤事項',
      dataIndex: 'ft163_11',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <TextArea rows={2} size="small" value={v} onChange={e => handleChange(idx, 'ft163_11', e.target.value)} />
        ) : v,
    },
    {
      title: '到期日',
      dataIndex: 'ft163_12',
      width: 130,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <DatePicker
            size="small"
            value={v ? dayjs(v) : null}
            onChange={d => handleChange(idx, 'ft163_12', d ? d.format('YYYY-MM-DD') : '')}
            style={{ width: '100%' }}
          />
        ) : v,
    },
    {
      title: '負責人代碼',
      dataIndex: 'ft163_15',
      width: 100,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input size="small" value={v} maxLength={8} onChange={e => handleChange(idx, 'ft163_15', e.target.value)} />
        ) : v,
    },
    {
      title: '處理情況',
      dataIndex: 'ft163_13',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <TextArea rows={2} size="small" value={v} onChange={e => handleChange(idx, 'ft163_13', e.target.value)} />
        ) : v,
    },
    ...(isEditing
      ? [{
          title: '',
          width: 40,
          render: (_: unknown, __: unknown, idx: number) => (
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(idx)} />
          ),
        }]
      : []),
  ];

  return (
    <Modal
      open
      title={`追蹤事項 — 議題 ${topicIdx + 1}: ${topicTitle || '(無標題)'}`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>關閉</Button>}
      width={800}
    >
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>新增追蹤事項</Button>
        </Space>
      )}
      <Table
        columns={columns}
        dataSource={items.map((row, i) => ({ ...row, key: i }))}
        size="small"
        pagination={false}
        locale={{ emptyText: '尚無追蹤事項' }}
      />
    </Modal>
  );
};

interface Props {
  topics: FT162FormItem[];
  isEditing: boolean;
  setField: <K extends keyof T206FormData>(field: K, value: T206FormData[K]) => void;
}

const Tab4Topics: React.FC<Props> = ({ topics, isEditing, setField }) => {
  const [trackingTopicIdx, setTrackingTopicIdx] = useState<number | null>(null);

  const handleChange = useCallback((idx: number, key: keyof Omit<FT162FormItem, 'tracking_items'>, value: string) => {
    const updated = topics.map((row, i) =>
      i === idx ? { ...row, [key]: value } : row
    );
    setField('topics', updated);
  }, [topics, setField]);

  const handleAdd = useCallback(() => {
    setField('topics', [
      ...topics,
      { ft162_11: '', ft162_12: '', ft162_13: '', tracking_items: [] },
    ]);
  }, [topics, setField]);

  const handleDelete = useCallback((idx: number) => {
    setField('topics', topics.filter((_, i) => i !== idx));
  }, [topics, setField]);

  const handleTrackingChange = useCallback((topicIdx: number, items: FT163FormItem[]) => {
    const updated = topics.map((row, i) =>
      i === topicIdx ? { ...row, tracking_items: items } : row
    );
    setField('topics', updated);
  }, [topics, setField]);

  const columns = [
    { title: '序', dataIndex: 'ft162_02', width: 40, render: (v: number, _: unknown, idx: number) => v ?? idx + 1 },
    {
      title: '討論內容',
      dataIndex: 'ft162_11',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <TextArea rows={2} size="small" value={v} onChange={e => handleChange(idx, 'ft162_11', e.target.value)} />
        ) : v,
    },
    {
      title: '決議事項',
      dataIndex: 'ft162_12',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <TextArea rows={2} size="small" value={v} onChange={e => handleChange(idx, 'ft162_12', e.target.value)} />
        ) : v,
    },
    {
      title: '負責人代碼',
      dataIndex: 'ft162_13',
      width: 110,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input size="small" value={v} maxLength={8} onChange={e => handleChange(idx, 'ft162_13', e.target.value)} />
        ) : v,
    },
    {
      title: '追蹤事項',
      width: 90,
      render: (_: unknown, row: FT162FormItem & { key: number }, idx: number) => (
        <Button
          size="small"
          icon={<UnorderedListOutlined />}
          onClick={() => setTrackingTopicIdx(idx)}
        >
          {row.tracking_items?.length || 0}
        </Button>
      ),
    },
    ...(isEditing
      ? [{
          title: '',
          width: 40,
          render: (_: unknown, __: unknown, idx: number) => (
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(idx)} />
          ),
        }]
      : []),
  ];

  return (
    <div>
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>新增議題</Button>
        </Space>
      )}
      <Table
        columns={columns}
        dataSource={topics.map((row, i) => ({ ...row, key: i }))}
        size="small"
        pagination={false}
        scroll={{ x: 700 }}
        locale={{ emptyText: '尚無議題' }}
      />
      {trackingTopicIdx !== null && (
        <TrackingModal
          topicIdx={trackingTopicIdx}
          topicTitle={topics[trackingTopicIdx]?.ft162_11 || ''}
          items={topics[trackingTopicIdx]?.tracking_items || []}
          isEditing={isEditing}
          onClose={() => setTrackingTopicIdx(null)}
          onChange={handleTrackingChange}
        />
      )}
    </div>
  );
};

export default Tab4Topics;
